import { groupCallsByPerson } from "../../call_groups";
import { callAppointmentDetails } from "../../call_appointment";
import {
  resolveCallsPageSelection,
  type CallsPageSelection,
} from "../../calls_selection";
import { scheduleScrollToLatest } from "../../scroll_to_latest";
import type { AppData } from "../../types";
import { el, statusLabel } from "../atoms/dom";
import { duration, empty, initials, relativeDate } from "./dashboard_page";

export function callsPage(
  data: AppData,
  onOpenAppointment: (appointmentId: string) => void,
  initialCallId?: string,
  persistedSelection?: CallsPageSelection | null,
  onSelectionChange?: (selection: CallsPageSelection) => void,
) {
  const groups = groupCallsByPerson(data.conversations);
  const detail = el("section", { className: "conversation-detail" });
  const list = el("div", { className: "conversation-list" });
  const initialSelection = resolveCallsPageSelection(
    groups,
    initialCallId,
    persistedSelection,
  );
  let selectedGroupKey = initialSelection.groupKey;
  let selectedCallId = initialSelection.callId;

  function persistSelection() {
    onSelectionChange?.({
      groupKey: selectedGroupKey,
      callId: selectedCallId,
    });
  }

  function selectGroup(key: string) {
    selectedGroupKey = key;
    selectedCallId = null;
    persistSelection();
    updateSelectedGroup();
    renderDetail();
  }

  function updateSelectedGroup() {
    list.querySelectorAll<HTMLButtonElement>(".conversation-item").forEach(
      (node) => node.classList.toggle(
        "is-active",
        node.dataset.groupKey === selectedGroupKey,
      ),
    );
  }

  function renderDetail() {
    const group = groups.find((item) => item.key === selectedGroupKey);
    if (!group) {
      detail.replaceChildren(empty("Henüz telefon görüşmesi bulunmuyor."));
      return;
    }
    const filters = buildCallFilters(group.calls);
    const visibleCalls = selectedCallId
      ? group.calls.filter((item) => item.id === selectedCallId)
      : [...group.calls].reverse();
    const transcript = el("div", {
      className: "transcript transcript--grouped",
    });
    for (const call of visibleCalls) {
      transcript.append(buildCallSession(call, group.displayName));
    }
    detail.replaceChildren(
      el(
        "header",
        { className: "conversation-detail__header" },
        el("span", {
          className: "call-avatar",
          text: initials(group.displayName),
        }),
        el(
          "div",
          {},
          el("h2", { text: group.displayName }),
          el("a", {
            text: group.phoneNumber,
            attrs: { href: `tel:${group.phoneNumber}` },
          }),
        ),
        el("span", {
          className: "conversation-count",
          text: `${group.calls.length} görüşme`,
        }),
      ),
      filters,
      transcript,
    );
    if (visibleCalls.some((call) => isLiveStatus(call.status))) {
      scheduleScrollToLatest(transcript);
    }
  }

  function buildCallFilters(calls: AppData["conversations"]) {
    const root = el(
      "div",
      { className: "call-filter-bar" },
      el(
        "div",
        { className: "call-filter-bar__label" },
        el("strong", { text: "Görüşmeler" }),
        el("span", {
          text: selectedCallId
            ? "Seçili görüşme gösteriliyor"
            : "Tüm konuşmalar tek akışta",
        }),
      ),
    );
    const scroller = el("div", { className: "call-filters" });
    scroller.append(
      el("button", {
        className: `call-filter ${selectedCallId === null ? "is-active" : ""}`,
        text: "Tümü",
        attrs: {
          type: "button",
          "aria-pressed": String(selectedCallId === null),
        },
        onClick: () => {
          selectedCallId = null;
          persistSelection();
          renderDetail();
        },
      }),
    );
    for (const call of calls) {
      const active = selectedCallId === call.id;
      scroller.append(
        el("button", {
          className: `call-filter ${active ? "is-active" : ""}`,
          text: filterDate(call.startedAt),
          attrs: {
            type: "button",
            "aria-pressed": String(active),
            title: `${relativeDate(call.startedAt)} tarihli görüşmeyi göster`,
          },
          onClick: () => {
            selectedCallId = active ? null : call.id;
            persistSelection();
            renderDetail();
          },
        }),
      );
    }
    root.append(scroller);
    return root;
  }

  function buildCallSession(
    call: AppData["conversations"][number],
    displayName: string,
  ) {
    const messages = data.conversationMessages[call.id] ?? [];
    const appointment = callAppointmentDetails(data, call);
    const body = el("section", {
      className: `call-session ${isLiveStatus(call.status) ? "is-live" : ""}`,
      attrs: { "data-call-id": call.id },
    });
    body.append(
      el(
        "div",
        { className: "call-session__divider" },
        el("span", { text: fullCallDate(call.startedAt) }),
      ),
      el(
        "div",
        { className: "call-session__summary" },
        el(
          "div",
          {},
          el("strong", { text: "Telefon görüşmesi" }),
          el("span", {
            text: `${duration(call.durationSeconds)} · ${resultLabel(call.result)}`,
          }),
        ),
        statusLabel(call.status),
        el("p", {
          text: call.summary || "Bu görüşme için henüz özet bulunmuyor.",
        }),
        appointment
          ? buildAppointmentLink(appointment)
          : call.result === "booking_confirmed"
            ? el("p", {
              className: "call-appointment-missing",
              text: "Bu görüşmeye bağlı randevu bilgisi bulunamadı.",
            })
            : null,
      ),
    );
    if (messages.length === 0) {
      body.append(
        el("p", {
          className: "call-session__empty",
          text: "Konuşma dökümü bulunmuyor.",
        }),
      );
      return body;
    }
    for (const message of messages) {
      body.append(
        el(
          "div",
          { className: `message message--${message.role}` },
          el("span", {
            text: message.role === "assistant" ? "AI Asistan" : displayName,
          }),
          el("p", { text: message.text }),
          message.at ? el("time", { text: messageTime(message.at) }) : null,
        ),
      );
    }
    return body;
  }

  function buildAppointmentLink(
    appointment: NonNullable<ReturnType<typeof callAppointmentDetails>>,
  ) {
    return el(
      "button",
      {
        className: "call-appointment-link",
        attrs: {
          type: "button",
          title: "Randevu detayını aç",
          "aria-label": `${appointment.customerName} için ${appointment.serviceName} randevusunu aç`,
        },
        onClick: () => onOpenAppointment(appointment.id),
      },
      el(
        "div",
        { className: "call-appointment-link__heading" },
        el("strong", { text: "Randevu oluşturuldu" }),
        statusLabel(appointment.status),
      ),
      el("span", {
        className: "call-appointment-link__service",
        text: appointment.serviceName,
      }),
      el("time", {
        text: appointmentDateTime(appointment.startsAt, appointment.endsAt),
      }),
      el("small", {
        text: [appointment.customerName, appointment.staffName]
          .filter(Boolean)
          .join(" · "),
      }),
      el("b", { text: "Randevuya git →" }),
    );
  }

  for (const group of groups) {
    const latestMessage = data.conversationMessages[group.latestCall.id]?.at(-1);
    list.append(
      el(
        "button",
        {
          className: `conversation-item ${group.key === selectedGroupKey ? "is-active" : ""}`,
          attrs: {
            type: "button",
            "data-group-key": group.key,
            "aria-label": `${group.displayName}, ${group.calls.length} görüşme`,
          },
          onClick: () => selectGroup(group.key),
        },
        el("span", {
          className: "call-avatar",
          text: initials(group.displayName),
        }),
        el(
          "div",
          {},
          el(
            "div",
            { className: "conversation-item__title" },
            el("strong", { text: group.displayName }),
            group.calls.length > 1
              ? el("span", {
                  className: "conversation-item__badge",
                  text: String(group.calls.length),
                })
              : null,
          ),
          el("span", { text: group.phoneNumber }),
          el("p", {
            text:
              latestMessage?.text ||
              group.latestCall.summary ||
              "Telefon görüşmesi",
          }),
        ),
        el("time", { text: listDate(group.latestCall.startedAt) }),
      ),
    );
  }
  persistSelection();
  updateSelectedGroup();
  renderDetail();
  return el(
    "div",
    { className: "calls-layout" },
    el(
      "section",
      { className: "calls-sidebar" },
      el(
        "header",
        {},
        el("h2", { text: "Kişiler" }),
        el("span", {
          text: `${groups.length} kişi · ${data.conversations.length} görüşme`,
        }),
      ),
      list,
    ),
    detail,
  );
}

function filterDate(value: string) {
  return new Intl.DateTimeFormat("tr-TR", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function fullCallDate(value: string) {
  return new Intl.DateTimeFormat("tr-TR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function listDate(value: string) {
  const date = new Date(value);
  const today = new Date();
  const sameDay = date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();
  return new Intl.DateTimeFormat(
    "tr-TR",
    sameDay
      ? { hour: "2-digit", minute: "2-digit" }
      : { day: "numeric", month: "short" },
  ).format(date);
}

function messageTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("tr-TR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function resultLabel(value: string) {
  return value === "booking_confirmed"
    ? "Randevu oluşturuldu"
    : "Bilgi görüşmesi";
}

function isLiveStatus(value: string) {
  return ["connected", "in_progress", "ringing", "queued", "forwarding"]
    .includes(value);
}

function appointmentDateTime(startsAt: string, endsAt: string) {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const date = new Intl.DateTimeFormat("tr-TR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(start);
  const time = new Intl.DateTimeFormat("tr-TR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date} · ${time.format(start)}–${time.format(end)}`;
}
